import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Menu,
  Stack,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import { IconDots } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { AdminData, queryKeys, RestApi } from "../shared/RestApi";
import { getCurrentUser } from "../shared/getCurrentUser";
import { NavBarContainer } from "./NavBarContainer";

type AdminUser = AdminData["users"][number];
type AdminDistributor = AdminData["distributors"][number];
type AdminPayment = AdminData["distributorPayments"][number];
type AdminDemoDownloader = AdminData["demoDownloaders"][number];

function formatPaymentSummary(u: {
  PaymentId?: string;
  PaymentAmount?: number;
  PaymentCurrency?: string;
}): string {
  return [u.PaymentId, u.PaymentAmount, u.PaymentCurrency]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .join(" ");
}

function UsersTab({
  users,
  onToggleBought,
  toggleBoughtPending,
  onToggleAdmin,
  toggleAdminPending,
}: {
  users: AdminUser[];
  onToggleBought: () => void;
  toggleBoughtPending: boolean;
  onToggleAdmin: (userId: string) => void;
  toggleAdminPending: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const sortedUsers = React.useMemo(
    () =>
      [...users].sort((a, b) => {
        const timeA = a.Timestamp ? new Date(a.Timestamp).getTime() : 0;
        const timeB = b.Timestamp ? new Date(b.Timestamp).getTime() : 0;
        return timeB - timeA;
      }),
    [users],
  );

  return (
    <Table.ScrollContainer minWidth={900}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>User</Table.Th>
            <Table.Th>Registration</Table.Th>
            <Table.Th>License</Table.Th>
            <Table.Th style={{ maxWidth: 120 }}>Payment</Table.Th>
            <Table.Th>Payment method</Table.Th>
            <Table.Th w={40} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedUsers.map((u) => (
            <Table.Tr
              key={u.UserId}
              onClick={() =>
                navigate(`/admin/user/${encodeURIComponent(u.UserId)}`)
              }
              style={{ cursor: "pointer" }}
            >
              <Table.Td>
                <Group gap="xs" wrap="wrap">
                  <Anchor
                    component={Link}
                    to={`/admin/user/${encodeURIComponent(u.UserId)}`}
                    fw={600}
                    size="sm"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {u.Email || u.Name}
                  </Anchor>
                  {u.EmailConfirmed && (
                    <Badge color="green" variant="light" size="sm">
                      Confirmed
                    </Badge>
                  )}
                  {u.IsAdmin && (
                    <Badge color="blue" variant="light" size="sm">
                      Admin
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                {u.Timestamp ? new Date(u.Timestamp).toLocaleString() : ""}
              </Table.Td>
              <Table.Td>{u.Bought ? "✓" : "✗"}</Table.Td>
              <Table.Td style={{ maxWidth: 120 }}>
                <Text truncate="end" size="sm" title={formatPaymentSummary(u)}>
                  {formatPaymentSummary(u)}
                </Text>
              </Table.Td>
              <Table.Td>{u.PaymentMethod}</Table.Td>
              <Table.Td onClick={(event) => event.stopPropagation()}>
                <Menu shadow="md" width={240} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label="User actions"
                      loading={toggleBoughtPending || toggleAdminPending}
                    >
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      disabled={u.UserId !== currentUser?.UserId}
                      onClick={onToggleBought}
                    >
                      Toggle Bought (cancels sub)
                    </Menu.Item>
                    <Menu.Item onClick={() => onToggleAdmin(u.UserId)}>
                      {t("admin.toggleAdmin")}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function DistributorsTab({
  distributors,
  onAuthorize,
  authorizePending,
}: {
  distributors: AdminDistributor[];
  onAuthorize: (userId: string) => void;
  authorizePending: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Table.ScrollContainer minWidth={800}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>UserId</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Company</Table.Th>
            <Table.Th>Available</Table.Th>
            <Table.Th>Bought</Table.Th>
            <Table.Th>Authorized</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {distributors.map((d) => (
            <Table.Tr key={d.DistributorInfo.RowKey}>
              <Table.Td>{d.CloudUser?.UserId}</Table.Td>
              <Table.Td>{d.CloudUser?.Email}</Table.Td>
              <Table.Td>{d.DistributorInfo.CompanyName}</Table.Td>
              <Table.Td>{d.AvailableLicenses}</Table.Td>
              <Table.Td>{d.LicensesBought}</Table.Td>
              <Table.Td>
                {d.DistributorInfo.DistribuidorAutorizado ? (
                  "✓"
                ) : (
                  <Button
                    size="xs"
                    loading={authorizePending}
                    onClick={() =>
                      onAuthorize(
                        d.CloudUser?.UserId || d.DistributorInfo.RowKey,
                      )
                    }
                  >
                    {t("admin.authorize")}
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function PaymentsTab({
  payments,
  distributors,
  users,
}: {
  payments: AdminPayment[];
  distributors: AdminDistributor[];
  users: AdminUser[];
}) {
  return (
    <Table.ScrollContainer minWidth={800}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>UserId</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>PaymentId</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Licenses</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {payments.map((p) => {
            const email =
              distributors.find(
                (d) =>
                  d.DistributorInfo.RowKey === p.PartitionKey ||
                  d.CloudUser?.UserId === p.PartitionKey,
              )?.CloudUser?.Email ||
              users.find((u) => u.UserId === p.PartitionKey)?.Email;
            return (
              <Table.Tr key={p.PaymentId}>
                <Table.Td>{p.PartitionKey}</Table.Td>
                <Table.Td>{email}</Table.Td>
                <Table.Td>{p.PaymentId}</Table.Td>
                <Table.Td>
                  {new Date(p.PaymentDate).toLocaleString()}
                </Table.Td>
                <Table.Td>
                  {p.PaymentAmount} {p.PaymentCurrency}
                </Table.Td>
                <Table.Td>{p.LicenseCount}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function DemoDownloadersTab({
  demoDownloaders,
}: {
  demoDownloaders: AdminDemoDownloader[];
}) {
  return (
    <Table.ScrollContainer minWidth={400}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time</Table.Th>
            <Table.Th>Email</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {demoDownloaders.map((d) => (
            <Table.Tr key={d.RowKey}>
              <Table.Td>
                {d.DownloadedAtUtc
                  ? new Date(d.DownloadedAtUtc).toLocaleString()
                  : ""}
              </Table.Td>
              <Table.Td>{d.Email}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function TabLabel({
  label,
  count,
}: {
  label: string;
  count: number;
}) {
  return (
    <Group gap="xs" wrap="nowrap">
      <span>{label}</span>
      <Badge size="sm" variant="light" circle>
        {count}
      </Badge>
    </Group>
  );
}

export function AdminPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.admin,
    queryFn: () => RestApi.getAdminInfo(),
  });

  const authorizeMutation = useMutation({
    mutationFn: (userId: string) => RestApi.authorizeDistributor(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
  });

  const toggleBoughtMutation = useMutation({
    mutationFn: () => RestApi.toggleBought(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
  });

  const toggleAdminMutation = useMutation({
    mutationFn: (userId: string) => RestApi.toggleAdmin(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
  });

  return (
    <NavBarContainer title={t("admin.title")}>
      <Stack gap="lg">
        <Group gap="md">
          <Button
            component="a"
            href="https://portal.azure.com/#@ecaservidordincloudoutlook.onmicrosoft.com/dashboard/arm/subscriptions/94f79f8f-9726-41b8-9ae4-bf378e5854d2/resourcegroups/dashboards/providers/microsoft.portal/dashboards/7c570d6f-79f7-43bb-b2d8-ca76fdc6e51e"
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
          >
            {t("admin.dataLink")}
          </Button>
          <Button
            component="a"
            href="https://portal.azure.com/#@ecaservidordincloudoutlook.onmicrosoft.com/resource/subscriptions/94f79f8f-9726-41b8-9ae4-bf378e5854d2/resourceGroups/DinLabel/providers/microsoft.insights/components/dinlabel/overview"
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
          >
            {t("admin.serverAnalyticsLink")}
          </Button>
          <Button component={Link} to="/admin/fakeemail" variant="light">
            {t("admin.fakeEmailLink")}
          </Button>
        </Group>
        {isError && <Text c="red">{t("admin.loadError")}</Text>}
        {isPending && <Text>{t("admin.loading")}</Text>}
        {data && (
          <>
            <Text>
              {t("admin.totalUsers", { count: data.users.length })} —{" "}
              {t("admin.paidUsers", {
                count: data.users.filter((u) => u.Bought).length,
              })}{" "}
              —{" "}
              {t("admin.totalSubscribedUsers", {
                count: data.totalSubscribedUsers,
              })}
            </Text>

            <Tabs defaultValue="users">
              <Tabs.List>
                <Tabs.Tab value="users">
                  <TabLabel label={t("admin.users")} count={data.users.length} />
                </Tabs.Tab>
                <Tabs.Tab value="distributors">
                  <TabLabel
                    label={t("admin.distributors")}
                    count={data.distributors.length}
                  />
                </Tabs.Tab>
                <Tabs.Tab value="payments">
                  <TabLabel
                    label={t("admin.payments")}
                    count={data.distributorPayments.length}
                  />
                </Tabs.Tab>
                <Tabs.Tab value="demoDownloaders">
                  <TabLabel
                    label={t("admin.demoDownloaders")}
                    count={data.demoDownloaders.length}
                  />
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="users" pt="md">
                <UsersTab
                  users={data.users}
                  onToggleBought={() => toggleBoughtMutation.mutate()}
                  toggleBoughtPending={toggleBoughtMutation.isPending}
                  onToggleAdmin={(userId) => toggleAdminMutation.mutate(userId)}
                  toggleAdminPending={toggleAdminMutation.isPending}
                />
              </Tabs.Panel>
              <Tabs.Panel value="distributors" pt="md">
                <DistributorsTab
                  distributors={data.distributors}
                  onAuthorize={(userId) => authorizeMutation.mutate(userId)}
                  authorizePending={authorizeMutation.isPending}
                />
              </Tabs.Panel>
              <Tabs.Panel value="payments" pt="md">
                <PaymentsTab
                  payments={data.distributorPayments}
                  distributors={data.distributors}
                  users={data.users}
                />
              </Tabs.Panel>
              <Tabs.Panel value="demoDownloaders" pt="md">
                <DemoDownloadersTab demoDownloaders={data.demoDownloaders} />
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </Stack>
    </NavBarContainer>
  );
}
